import React, { useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { ImageUploadService } from '../../../accounting/inventory/services/imageUploadService';
import {
  CORPORATE_HERO_MAX_PRIMARY_BUTTONS,
  CORPORATE_HERO_MAX_SECONDARY_BUTTONS,
  CORPORATE_HERO_MAX_SLIDES,
  CORPORATE_HERO_MAX_STATS_STRIP,
  type CorporateHeroAnimationType,
  type CorporateHeroButtonAction,
  type CorporateHeroButtonItem,
  type CorporateHeroSectionOption,
  type CorporateHeroSectionSettings,
  type CorporateHeroSlideItem,
  type CorporateHeroStatItem,
  type CorporateHeroTemplateType,
  type CorporateHeroTextAlign,
} from '../../../../types/corporateHero';
import CorporateHeroTemplatePicker from './CorporateHeroTemplatePicker';
import CorporateHeroPreview from './CorporateHeroPreview';
import RichTextEditor from '../../../common/RichTextEditor';

const BUTTON_ACTIONS: CorporateHeroButtonAction[] = [
  'none',
  'scroll-to-section',
  'external-url',
  'contact-page',
];

const emptyButton = (): CorporateHeroButtonItem => ({
  label: '',
  action: 'none',
  target: '',
  enabled: true,
});

const emptySlide = (): CorporateHeroSlideItem => ({
  imageUrl: '',
  title: '',
});

const emptyStat = (): CorporateHeroStatItem => ({
  value: '',
  label: '',
});

const GRADIENT_PRESETS = [
  {
    key: 'aquaBlue',
    value: 'linear-gradient(135deg, rgba(13, 71, 161, 0.95) 0%, rgba(25, 118, 210, 0.85) 100%)',
  },
  { key: 'bluePurple', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { key: 'orangeRed', value: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { key: 'greenBlue', value: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
  { key: 'purplePink', value: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' },
  { key: 'goldOrange', value: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' },
  { key: 'nightBlue', value: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)' },
  { key: 'sunset', value: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)' },
  { key: 'forest', value: 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)' },
];

const SOLID_PRESETS = [
  { key: 'navy', value: '#0d47a1' },
  { key: 'blue', value: '#1976d2' },
  { key: 'teal', value: '#0d9488' },
  { key: 'slate', value: '#1e293b' },
  { key: 'indigo', value: '#4338ca' },
  { key: 'rose', value: '#e11d48' },
  { key: 'orange', value: '#ea580c' },
  { key: 'green', value: '#15803d' },
  { key: 'white', value: '#ffffff' },
  { key: 'light', value: '#f8fafc' },
];

const clampChannel = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
const toHexPair = (n: number) => clampChannel(n).toString(16).padStart(2, '0');

// Native <input type="color"> only accepts #rrggbb. This normalises any value the
// user may have typed (hex shorthand, rgb/rgba) into a hex the swatch can display.
const toHexColor = (value: string | undefined, fallback = '#000000'): string => {
  if (!value) return fallback;
  const trimmed = value.trim();

  const hexMatch = trimmed.match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (hexMatch) {
    const hex = hexMatch[1];
    if (hex.length === 3) {
      return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`.toLowerCase();
    }
    return `#${hex}`.toLowerCase();
  }

  const rgbMatch = trimmed.match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i);
  if (rgbMatch) {
    return `#${toHexPair(Number(rgbMatch[1]))}${toHexPair(Number(rgbMatch[2]))}${toHexPair(Number(rgbMatch[3]))}`;
  }

  return fallback;
};

const GRADIENT_DIRECTIONS = [
  { angle: 90, label: '→ 90°' },
  { angle: 135, label: '↘ 135°' },
  { angle: 180, label: '↓ 180°' },
  { angle: 225, label: '↙ 225°' },
  { angle: 270, label: '← 270°' },
  { angle: 315, label: '↖ 315°' },
  { angle: 0, label: '↑ 0°' },
  { angle: 45, label: '↗ 45°' },
];

const DEFAULT_GRADIENT_FROM = '#667eea';
const DEFAULT_GRADIENT_TO = '#764ba2';

const buildLinearGradient = (from: string, to: string, angle: number): string =>
  `linear-gradient(${angle}deg, ${from} 0%, ${to} 100%)`;

// Splits a comma-separated CSS list while keeping parentheses (e.g. rgba(...)) intact.
const splitTopLevel = (input: string): string[] => {
  const parts: string[] = [];
  let depth = 0;
  let current = '';
  for (const char of input) {
    if (char === '(') depth += 1;
    if (char === ')') depth -= 1;
    if (char === ',' && depth === 0) {
      parts.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  if (current.trim()) parts.push(current.trim());
  return parts;
};

const extractStopColor = (stop: string): string => stop.trim().split(/\s+/)[0];

interface ParsedGradient {
  angle: number;
  from: string;
  to: string;
}

const parseLinearGradient = (css: string | undefined): ParsedGradient | null => {
  if (!css) return null;
  const match = css.trim().match(/^linear-gradient\(([\s\S]*)\)$/i);
  if (!match) return null;

  const tokens = splitTopLevel(match[1]);
  if (tokens.length === 0) return null;

  let angle = 135;
  let colorTokens = tokens;

  const first = tokens[0];
  const degMatch = first.match(/^(-?[\d.]+)deg$/i);
  if (degMatch) {
    angle = Number(degMatch[1]);
    colorTokens = tokens.slice(1);
  } else if (/^to\s+/i.test(first)) {
    const dirMap: Record<string, number> = {
      'to top': 0,
      'to right': 90,
      'to bottom': 180,
      'to left': 270,
      'to top right': 45,
      'to bottom right': 135,
      'to bottom left': 225,
      'to top left': 315,
    };
    angle = dirMap[first.toLowerCase().replace(/\s+/g, ' ')] ?? 135;
    colorTokens = tokens.slice(1);
  }

  if (colorTokens.length === 0) return null;

  const from = extractStopColor(colorTokens[0]);
  const to = extractStopColor(colorTokens[colorTokens.length - 1]);
  return { angle, from, to };
};

const ANIMATION_OPTIONS: CorporateHeroAnimationType[] = [
  'none',
  'fade-up',
  'slide-left',
  'slide-right',
  'scale-on-scroll',
];

interface CorporateHeroSettingsPanelProps {
  value: CorporateHeroSectionSettings;
  onChange: (hero: CorporateHeroSectionSettings) => void;
  companyId?: string | null;
  defaultHeight?: number;
  /** Sayfa bölümleri — başlıkla seçim (scroll-to-section) */
  sectionOptions?: CorporateHeroSectionOption[];
}

const CorporateHeroSettingsPanel: React.FC<CorporateHeroSettingsPanelProps> = ({
  value,
  onChange,
  companyId,
  defaultHeight = 500,
  sectionOptions = [],
}) => {
  const { t } = useTranslation('salesSystem');
  const valueRef = useRef(value);
  const [uploadTarget, setUploadTarget] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const backgroundIsGradient = (value.backgroundColor || '').includes('gradient');
  const [bgMode, setBgMode] = useState<'solid' | 'gradient'>(
    backgroundIsGradient ? 'gradient' : 'solid'
  );
  const [showAdvancedBg, setShowAdvancedBg] = useState(false);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    setBgMode(backgroundIsGradient ? 'gradient' : 'solid');
  }, [backgroundIsGradient]);

  // Metin & Görsel şablonunda eski kayıtlardaki "center" hizasını sola çek
  useEffect(() => {
    if (
      value.templateType === 'split-text-image' &&
      (value.design.textAlign === 'center' || !value.design.textAlign)
    ) {
      onChange({
        ...value,
        design: { ...value.design, textAlign: 'left' },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca şablon/hizalama değişince
  }, [value.templateType, value.design.textAlign]);

  const defaultSecondaryFromSections = (): CorporateHeroButtonItem[] => {
    if (sectionOptions.length > 0) {
      return sectionOptions.slice(0, 3).map((option) => ({
        label: option.label.includes(' — ')
          ? option.label.split(' — ').slice(-1)[0]
          : option.label,
        action: 'scroll-to-section' as const,
        target: option.value,
        enabled: true,
      }));
    }
    return [
      { label: 'Özellikler', action: 'scroll-to-section', target: '', enabled: true },
      { label: 'SSS', action: 'scroll-to-section', target: '', enabled: true },
      { label: 'Başlayın', action: 'scroll-to-section', target: '', enabled: true },
    ];
  };

  const patch = (partial: Partial<CorporateHeroSectionSettings>) => {
    onChange({ ...value, ...partial });
  };

  const patchDesign = (partial: Partial<CorporateHeroSectionSettings['design']>) => {
    onChange({ ...value, design: { ...value.design, ...partial } });
  };

  const patchLayout = (partial: Partial<CorporateHeroSectionSettings['layout']>) => {
    onChange({ ...value, layout: { ...value.layout, ...partial } });
  };

  const parsedGradient = parseLinearGradient(value.backgroundColor);
  const gradientFrom = parsedGradient?.from || DEFAULT_GRADIENT_FROM;
  const gradientTo = parsedGradient?.to || DEFAULT_GRADIENT_TO;
  const gradientAngle = parsedGradient?.angle ?? 135;

  const updateGradient = (partial: Partial<ParsedGradient>) => {
    const next = {
      from: partial.from ?? gradientFrom,
      to: partial.to ?? gradientTo,
      angle: partial.angle ?? gradientAngle,
    };
    patch({ backgroundColor: buildLinearGradient(next.from, next.to, next.angle) });
  };

  const handleBgModeChange = (mode: 'solid' | 'gradient') => {
    setBgMode(mode);
    if (mode === 'gradient' && !backgroundIsGradient) {
      const base = (value.backgroundColor || '').trim();
      const from = /^#|^rgb/i.test(base) ? base : DEFAULT_GRADIENT_FROM;
      patch({ backgroundColor: buildLinearGradient(from, DEFAULT_GRADIENT_TO, 135) });
    } else if (mode === 'solid' && backgroundIsGradient) {
      patch({ backgroundColor: gradientFrom || '#fafafa' });
    }
  };

  const handleTemplateChange = (templateType: CorporateHeroTemplateType) => {
    const next: CorporateHeroSectionSettings = {
      ...value,
      templateType,
      design: {
        ...value.design,
        textAlign:
          templateType === 'minimal-centered'
            ? 'center'
            : templateType === 'image-overlay'
              ? 'left'
              : value.design.textAlign,
      },
    };

    if (templateType === 'minimal-centered' && value.backgroundColor.includes('gradient')) {
      next.backgroundColor = '#fafafa';
      next.design.titleColor = next.design.titleColor || '#1e293b';
      next.design.descriptionColor = next.design.descriptionColor || '#64748b';
    }

    if (templateType === 'image-overlay') {
      next.design.overlayOpacity = next.design.overlayOpacity ?? 0.65;
      next.design.titleColor = '#ffffff';
      next.design.descriptionColor = 'rgba(255,255,255,0.92)';
    }

    if (templateType === 'two-column-text') {
      next.backgroundColor = next.backgroundColor.includes('gradient') ? '#f0f7fa' : next.backgroundColor || '#f0f7fa';
      next.design.titleColor = next.design.titleColor === '#ffffff' ? '#0d9488' : next.design.titleColor || '#0d9488';
      next.design.descriptionColor = next.design.descriptionColor || '#64748b';
    }

    if (templateType === 'three-column-stats') {
      next.backgroundColor = next.backgroundColor.includes('gradient') ? '#f0f7fa' : next.backgroundColor || '#f0f7fa';
      next.design.titleColor = next.design.titleColor === '#ffffff' ? '#0d9488' : next.design.titleColor || '#0d9488';
      next.design.descriptionColor = next.design.descriptionColor || '#64748b';
    }

    if (templateType === 'cta-feature') {
      next.backgroundColor =
        'linear-gradient(135deg, rgba(13, 71, 161, 0.95) 0%, rgba(25, 118, 210, 0.85) 100%)';
      next.design.titleColor = '#ffffff';
      next.design.descriptionColor = 'rgba(255,255,255,0.9)';
      next.design.textAlign = 'center';
      next.height = Math.max(next.height || 500, 560);
      next.layout = {
        ...next.layout,
        showPrimaryButtons: true,
        showSecondaryButtons: true,
        showStatsStrip: true,
        showImageSlider: true,
        imagePosition: next.layout.imagePosition || 'right',
        primaryButtons:
          next.layout.primaryButtons && next.layout.primaryButtons.length > 0
            ? next.layout.primaryButtons
            : [
                { label: 'Ücretsiz Deneyin', action: 'contact-page', enabled: true },
                { label: 'Hemen Başlayın', action: 'contact-page', enabled: true },
              ],
        secondaryButtons:
          next.layout.secondaryButtons && next.layout.secondaryButtons.length > 0
            ? next.layout.secondaryButtons
            : defaultSecondaryFromSections(),
        stats:
          next.layout.stats && next.layout.stats.some((s) => s.value || s.label)
            ? next.layout.stats
            : [
                { value: '1000+', label: 'Aktif Site' },
                { value: '99.9%', label: 'Uptime' },
                { value: '7/24', label: 'Destek' },
                { value: '12+', label: 'Entegrasyon' },
              ],
        slides: next.layout.slides || [],
        sliderAutoplayMs: next.layout.sliderAutoplayMs || 5000,
      };
    }

    // Metin & Görsel: görsel solda, metin sağda (varsayılan)
    if (templateType === 'split-text-image') {
      next.layout = {
        ...next.layout,
        imagePosition: 'left',
      };
      next.design.textAlign = 'left';
      next.design.titleColor =
        next.design.titleColor === '#ffffff' ? '#1e293b' : next.design.titleColor || '#1e293b';
      next.design.descriptionColor =
        next.design.descriptionColor === 'rgba(255,255,255,0.92)' ||
        next.design.descriptionColor === 'rgba(255,255,255,0.9)'
          ? '#64748b'
          : next.design.descriptionColor || '#64748b';
      if (next.backgroundColor.includes('gradient')) {
        next.backgroundColor = '#f8fafc';
      }
    }

    onChange(next);
  };

  const uploadHeroImage = async (
    file: File,
    target: 'imageUrl' | 'backgroundImageUrl' | `slide:${number}`
  ) => {
    try {
      setUploading(true);
      setUploadTarget(target);
      setUploadError(null);

      const [uploaded] = await ImageUploadService.uploadImages([file]);
      if (!uploaded?.originalUrl) {
        throw new Error('Image upload returned empty URL');
      }

      const current = valueRef.current;
      if (target.startsWith('slide:')) {
        const slideIndex = Number(target.split(':')[1]);
        const slides = [...(current.layout.slides || [])];
        while (slides.length <= slideIndex) slides.push(emptySlide());
        slides[slideIndex] = { ...slides[slideIndex], imageUrl: uploaded.originalUrl };
        onChange({
          ...current,
          layout: { ...current.layout, slides },
        });
      } else {
        onChange({
          ...current,
          layout: {
            ...current.layout,
            [target]: uploaded.originalUrl,
          },
        });
      }
    } catch (error) {
      console.error('Hero image upload failed:', error);
      setUploadError(
        error instanceof Error && error.message
          ? error.message
          : t('ecommerceCorporateHero.uploadFailed')
      );
    } finally {
      setUploading(false);
      setUploadTarget(null);
    }
  };

  const renderColorField = (
    label: string,
    currentValue: string | undefined,
    onColorChange: (value: string) => void,
    placeholder?: string,
    swatchFallback = '#000000'
  ) => (
    <TextField
      fullWidth
      label={label}
      value={currentValue || ''}
      onChange={(e) => onColorChange(e.target.value)}
      placeholder={placeholder}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Box
              component="input"
              type="color"
              value={toHexColor(currentValue, swatchFallback)}
              onChange={(e) => onColorChange(e.target.value)}
              sx={{
                border: '1px solid rgba(0,0,0,0.2)',
                borderRadius: '4px',
                width: '28px',
                height: '28px',
                p: 0,
                background: 'none',
                cursor: 'pointer',
              }}
            />
          </InputAdornment>
        ),
      }}
    />
  );

  const renderImageField = (
    label: string,
    field: 'imageUrl' | 'backgroundImageUrl',
    url: string | undefined
  ) => (
    <Grid item xs={12}>
      <TextField
        fullWidth
        label={label}
        value={url || ''}
        onChange={(e) => patchLayout({ [field]: e.target.value })}
        placeholder="https://..."
      />
      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
            disabled={uploading}
          >
            {uploading && uploadTarget === field
              ? t('ecommerceCorporateHero.uploading')
              : t('ecommerceCorporateHero.uploadImage')}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => {
                const selectedFile = e.target.files?.[0];
                if (selectedFile) {
                  void uploadHeroImage(selectedFile, field);
                }
                e.target.value = '';
              }}
            />
          </Button>
        </Box>
    </Grid>
  );

  const needsImage = value.templateType === 'split-text-image';
  const needsBgImage = value.templateType === 'image-overlay';
  const needsTwoColumns = value.templateType === 'two-column-text';
  const needsStats = value.templateType === 'three-column-stats';
  const supportsFeatureBlocks =
    value.templateType === 'classic-gradient' || value.templateType === 'cta-feature';

  const heroEnabled = value.enabled !== false;

  const updatePrimaryButton = (index: number, partial: Partial<CorporateHeroButtonItem>) => {
    const buttons = [...(value.layout.primaryButtons || [])];
    while (buttons.length <= index) buttons.push(emptyButton());
    buttons[index] = { ...buttons[index], ...partial };
    patchLayout({ primaryButtons: buttons });
  };

  const updateSecondaryButton = (index: number, partial: Partial<CorporateHeroButtonItem>) => {
    const buttons = [...(value.layout.secondaryButtons || [])];
    while (buttons.length <= index) buttons.push(emptyButton());
    buttons[index] = { ...buttons[index], ...partial };
    patchLayout({ secondaryButtons: buttons });
  };

  const updateSlide = (index: number, partial: Partial<CorporateHeroSlideItem>) => {
    const slides = [...(value.layout.slides || [])];
    while (slides.length <= index) slides.push(emptySlide());
    slides[index] = { ...slides[index], ...partial };
    patchLayout({ slides });
  };

  const updateStripStat = (index: number, partial: Partial<CorporateHeroStatItem>) => {
    const stats = [...(value.layout.stats || [])];
    while (stats.length <= index) stats.push(emptyStat());
    stats[index] = { ...stats[index], ...partial };
    patchLayout({ stats });
  };

  const resolveSectionSelectValue = (
    target?: string
  ): CorporateHeroSectionOption | string | null => {
    if (!target) return null;
    const matched = sectionOptions.find((option) => option.value === target);
    if (matched) return matched;
    return target;
  };

  const renderButtonEditor = (
    button: CorporateHeroButtonItem,
    onPatch: (partial: Partial<CorporateHeroButtonItem>) => void,
    onRemove?: () => void
  ) => (
    <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          size="small"
          label={t('ecommerceCorporateHero.buttonLabel')}
          value={button.label || ''}
          onChange={(e) => onPatch({ label: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <FormControl fullWidth size="small">
          <InputLabel>{t('ecommerceCorporateHero.buttonAction')}</InputLabel>
          <Select
            label={t('ecommerceCorporateHero.buttonAction')}
            value={button.action || 'none'}
            onChange={(e) => onPatch({ action: e.target.value as CorporateHeroButtonAction })}
          >
            {BUTTON_ACTIONS.map((action) => (
              <MenuItem key={action} value={action}>
                {t(`ecommerceCorporateHero.buttonActions.${action}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        {button.action === 'scroll-to-section' ? (
          <Autocomplete
            freeSolo
            size="small"
            options={sectionOptions}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.label
            }
            isOptionEqualToValue={(option, selected) =>
              typeof option === 'string' || typeof selected === 'string'
                ? option === selected
                : option.value === selected.value
            }
            value={resolveSectionSelectValue(button.target)}
            onChange={(_e, newValue) => {
              if (typeof newValue === 'string') {
                onPatch({ target: newValue.trim() });
              } else if (newValue) {
                onPatch({ target: newValue.value });
              } else {
                onPatch({ target: '' });
              }
            }}
            onInputChange={(_e, inputValue, reason) => {
              if (reason === 'input') {
                onPatch({ target: inputValue });
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('ecommerceCorporateHero.buttonSection')}
                placeholder={t('ecommerceCorporateHero.buttonSectionPlaceholder')}
              />
            )}
          />
        ) : (
          <TextField
            fullWidth
            size="small"
            label={t('ecommerceCorporateHero.buttonUrl')}
            value={button.target || ''}
            onChange={(e) => onPatch({ target: e.target.value })}
            disabled={button.action === 'none' || button.action === 'contact-page'}
            placeholder="https://..."
          />
        )}
      </Grid>
      <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {onRemove && (
          <IconButton size="small" color="error" onClick={onRemove} aria-label="remove">
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </Grid>
    </Grid>
  );

  return (
    <Grid container spacing={3}>
      {uploadError && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setUploadError(null)}>
            {uploadError}
          </Alert>
        </Grid>
      )}

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={heroEnabled}
              onChange={(e) => patch({ enabled: e.target.checked })}
              color="primary"
            />
          }
          label={t('ecommerceCorporateHero.showHero')}
        />
        {!heroEnabled && (
          <Alert severity="info" sx={{ mt: 1 }}>
            {t('ecommerceCorporateHero.heroDisabledHint')}
          </Alert>
        )}
      </Grid>

      <Grid item xs={12} sx={{ opacity: heroEnabled ? 1 : 0.45, pointerEvents: heroEnabled ? 'auto' : 'none' }}>
      <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
          {t('ecommerceCorporateHero.templateLayout')}
        </Typography>
        <CorporateHeroTemplatePicker value={value.templateType} onChange={handleTemplateChange} />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label={t('ecommerceAboutSettings.fields.title')}
          value={value.title}
          onChange={(e) => patch({ title: e.target.value })}
          disabled={!heroEnabled}
        />
      </Grid>

      <Grid item xs={12}>
        <RichTextEditor
          label={t('ecommerceAboutSettings.fields.description')}
          value={value.description}
          onChange={(html) => patch({ description: html })}
          minHeight={140}
        />
      </Grid>

      {/* Metin & Görsel: görsel konumu / yan görsel / etiket — şablon seçiminin hemen altında */}
      {(needsImage || value.templateType === 'split-text-image') && (
        <>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('ecommerceCorporateHero.imagePosition')}</InputLabel>
              <Select
                label={t('ecommerceCorporateHero.imagePosition')}
                value={value.layout.imagePosition || 'left'}
                onChange={(e) => patchLayout({ imagePosition: e.target.value as 'left' | 'right' })}
              >
                <MenuItem value="left">{t('ecommerceCorporateHero.imageLeft')}</MenuItem>
                <MenuItem value="right">{t('ecommerceCorporateHero.imageRight')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {renderImageField(t('ecommerceCorporateHero.sideImage'), 'imageUrl', value.layout.imageUrl)}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t('ecommerceCorporateHero.badgeText')}
              value={value.design.badgeText || ''}
              onChange={(e) => patchDesign({ badgeText: e.target.value })}
              helperText={t('ecommerceCorporateHero.badgeTextHint')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            {renderColorField(
              t('ecommerceCorporateHero.badgeColor'),
              value.design.badgeColor,
              (color) => patchDesign({ badgeColor: color })
            )}
          </Grid>
        </>
      )}

      {!needsBgImage && (
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            {t('ecommerceCorporateHero.backgroundPicker')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('ecommerceCorporateHero.backgroundPickerHint')}
          </Typography>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={bgMode}
            onChange={(_e, mode) => mode && handleBgModeChange(mode)}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="solid">{t('ecommerceAboutSettings.fields.bgTypeSolid')}</ToggleButton>
            <ToggleButton value="gradient">{t('ecommerceAboutSettings.fields.bgTypeGradient')}</ToggleButton>
          </ToggleButtonGroup>

          {bgMode === 'solid' && (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 2 }}>
                {SOLID_PRESETS.map((color) => {
                  const selected =
                    toHexColor(value.backgroundColor, '').toLowerCase() === color.value.toLowerCase();
                  return (
                    <Box
                      key={color.key}
                      component="button"
                      type="button"
                      title={t(`ecommerceCorporateHero.solidColors.${color.key}`)}
                      onClick={() => patch({ backgroundColor: color.value })}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1.5,
                        border: selected ? '3px solid #1976d2' : '1px solid rgba(0,0,0,0.18)',
                        background: color.value,
                        cursor: 'pointer',
                        boxShadow: selected ? '0 0 0 2px rgba(25,118,210,0.25)' : 'none',
                        p: 0,
                      }}
                    />
                  );
                })}
                <Box
                  component="label"
                  title={t('ecommerceCorporateHero.pickCustomColor')}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 1.5,
                    border: '1px dashed rgba(0,0,0,0.35)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    background: toHexColor(
                      backgroundIsGradient ? gradientFrom : value.backgroundColor,
                      '#1976d2'
                    ),
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#fff',
                      fontWeight: 700,
                      textShadow: '0 1px 2px rgba(0,0,0,0.45)',
                      pointerEvents: 'none',
                    }}
                  >
                    +
                  </Typography>
                  <Box
                    component="input"
                    type="color"
                    value={toHexColor(
                      backgroundIsGradient ? gradientFrom : value.backgroundColor,
                      '#1976d2'
                    )}
                    onChange={(e) => patch({ backgroundColor: e.target.value })}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      opacity: 0,
                      cursor: 'pointer',
                      border: 0,
                      p: 0,
                      width: '100%',
                      height: '100%',
                    }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  height: 40,
                  borderRadius: 1,
                  border: '1px solid rgba(0,0,0,0.12)',
                  background: backgroundIsGradient
                    ? toHexColor(gradientFrom, '#1976d2')
                    : value.backgroundColor || '#fafafa',
                }}
              />
            </>
          )}

          {bgMode === 'gradient' && (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 2 }}>
                {GRADIENT_PRESETS.map((gradient) => {
                  const selected = value.backgroundColor === gradient.value;
                  return (
                    <Box
                      key={gradient.key}
                      component="button"
                      type="button"
                      title={t(`ecommerceAboutSettings.gradients.${gradient.key}`)}
                      onClick={() => patch({ backgroundColor: gradient.value })}
                      sx={{
                        width: 72,
                        height: 44,
                        borderRadius: 1.5,
                        border: selected ? '3px solid #1976d2' : '1px solid rgba(0,0,0,0.18)',
                        background: gradient.value,
                        cursor: 'pointer',
                        boxShadow: selected ? '0 0 0 2px rgba(25,118,210,0.25)' : 'none',
                        p: 0,
                      }}
                    />
                  );
                })}
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t('ecommerceCorporateHero.gradientDirectionHint')}
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={gradientAngle}
                onChange={(_e, angle) => angle != null && updateGradient({ angle })}
                sx={{ mb: 2, flexWrap: 'wrap' }}
              >
                {GRADIENT_DIRECTIONS.map((dir) => (
                  <ToggleButton key={dir.angle} value={dir.angle} sx={{ px: 1.25 }}>
                    {dir.label.split(' ')[0]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">{t('ecommerceAboutSettings.fields.gradientStart')}</Typography>
                  <Box
                    component="input"
                    type="color"
                    value={toHexColor(gradientFrom, DEFAULT_GRADIENT_FROM)}
                    onChange={(e) => updateGradient({ from: e.target.value })}
                    sx={{
                      width: 40,
                      height: 40,
                      border: '1px solid rgba(0,0,0,0.2)',
                      borderRadius: 1,
                      p: 0,
                      background: 'none',
                      cursor: 'pointer',
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">{t('ecommerceAboutSettings.fields.gradientEnd')}</Typography>
                  <Box
                    component="input"
                    type="color"
                    value={toHexColor(gradientTo, DEFAULT_GRADIENT_TO)}
                    onChange={(e) => updateGradient({ to: e.target.value })}
                    sx={{
                      width: 40,
                      height: 40,
                      border: '1px solid rgba(0,0,0,0.2)',
                      borderRadius: 1,
                      p: 0,
                      background: 'none',
                      cursor: 'pointer',
                    }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  height: 48,
                  borderRadius: 1,
                  border: '1px solid rgba(0,0,0,0.12)',
                  background: buildLinearGradient(gradientFrom, gradientTo, gradientAngle),
                }}
              />
            </>
          )}

          <Accordion
            disableGutters
            elevation={0}
            expanded={showAdvancedBg}
            onChange={(_e, expanded) => setShowAdvancedBg(expanded)}
            sx={{ mt: 1.5, border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight={600}>
                {t('ecommerceCorporateHero.advancedBackground')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                fullWidth
                size="small"
                label={t('ecommerceAboutSettings.fields.customBackgroundCss')}
                value={value.backgroundColor}
                onChange={(e) => patch({ backgroundColor: e.target.value })}
                multiline
                rows={2}
                helperText={t('ecommerceAboutSettings.fields.customBackgroundCssHelper')}
              />
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}

      <Grid item xs={12} md={4}>
        {renderColorField(
          t('ecommerceCorporateHero.titleColor'),
          value.design.titleColor,
          (color) => patchDesign({ titleColor: color }),
          '#ffffff',
          '#ffffff'
        )}
      </Grid>
      <Grid item xs={12} md={4}>
        {renderColorField(
          t('ecommerceCorporateHero.descriptionColor'),
          value.design.descriptionColor,
          (color) => patchDesign({ descriptionColor: color }),
          'rgba(255,255,255,0.9)',
          '#ffffff'
        )}
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>{t('ecommerceCorporateHero.textAlign')}</InputLabel>
          <Select
            label={t('ecommerceCorporateHero.textAlign')}
            value={
              value.templateType === 'split-text-image'
                ? value.design.textAlign === 'right'
                  ? 'right'
                  : 'left'
                : value.design.textAlign || 'center'
            }
            onChange={(e) => patchDesign({ textAlign: e.target.value as CorporateHeroTextAlign })}
          >
            <MenuItem value="left">{t('ecommerceCorporateHero.alignLeft')}</MenuItem>
            {value.templateType !== 'split-text-image' && (
              <MenuItem value="center">{t('ecommerceCorporateHero.alignCenter')}</MenuItem>
            )}
            <MenuItem value="right">{t('ecommerceCorporateHero.alignRight')}</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {supportsFeatureBlocks && (
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            {t('ecommerceCorporateHero.featureBlocks')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('ecommerceCorporateHero.featureBlocksHint')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!value.layout.showPrimaryButtons}
                        onChange={(e) => {
                          const enabled = e.target.checked;
                          patchLayout({
                            showPrimaryButtons: enabled,
                            primaryButtons:
                              enabled && (!value.layout.primaryButtons || value.layout.primaryButtons.length === 0)
                                ? [
                                    { label: 'Ücretsiz Deneyin', action: 'contact-page', enabled: true },
                                    { label: 'Hemen Başlayın', action: 'contact-page', enabled: true },
                                  ]
                                : value.layout.primaryButtons,
                          });
                        }}
                      />
                    }
                    label={t('ecommerceCorporateHero.primaryButtons')}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    {t('ecommerceCorporateHero.primaryButtonsHint')}
                  </Typography>
                  {value.layout.showPrimaryButtons &&
                    (value.layout.primaryButtons || []).map((button, index) => (
                      <Box key={`primary-${index}`}>
                        {renderButtonEditor(button, (partial) => updatePrimaryButton(index, partial), () => {
                          const next = [...(value.layout.primaryButtons || [])];
                          next.splice(index, 1);
                          patchLayout({ primaryButtons: next });
                        })}
                      </Box>
                    ))}
                  {value.layout.showPrimaryButtons &&
                    (value.layout.primaryButtons || []).length < CORPORATE_HERO_MAX_PRIMARY_BUTTONS && (
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() =>
                          patchLayout({
                            primaryButtons: [...(value.layout.primaryButtons || []), emptyButton()],
                          })
                        }
                      >
                        {t('ecommerceCorporateHero.addButton')}
                      </Button>
                    )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!value.layout.showSecondaryButtons}
                        onChange={(e) => {
                          const enabled = e.target.checked;
                          patchLayout({
                            showSecondaryButtons: enabled,
                            secondaryButtons:
                              enabled &&
                              (!value.layout.secondaryButtons || value.layout.secondaryButtons.length === 0)
                                ? defaultSecondaryFromSections()
                                : value.layout.secondaryButtons,
                          });
                        }}
                      />
                    }
                    label={t('ecommerceCorporateHero.secondaryButtons')}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    {t('ecommerceCorporateHero.secondaryButtonsHint')}
                  </Typography>
                  {value.layout.showSecondaryButtons && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                      {sectionOptions.length > 0
                        ? t('ecommerceCorporateHero.buttonSectionHint')
                        : t('ecommerceCorporateHero.buttonSectionHintEmpty')}
                    </Typography>
                  )}
                  {value.layout.showSecondaryButtons &&
                    (value.layout.secondaryButtons || []).map((button, index) => (
                      <Box key={`secondary-${index}`}>
                        {renderButtonEditor(button, (partial) => updateSecondaryButton(index, partial), () => {
                          const next = [...(value.layout.secondaryButtons || [])];
                          next.splice(index, 1);
                          patchLayout({ secondaryButtons: next });
                        })}
                      </Box>
                    ))}
                  {value.layout.showSecondaryButtons &&
                    (value.layout.secondaryButtons || []).length < CORPORATE_HERO_MAX_SECONDARY_BUTTONS && (
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() =>
                          patchLayout({
                            secondaryButtons: [...(value.layout.secondaryButtons || []), emptyButton()],
                          })
                        }
                      >
                        {t('ecommerceCorporateHero.addButton')}
                      </Button>
                    )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!value.layout.showStatsStrip}
                        onChange={(e) => {
                          const enabled = e.target.checked;
                          patchLayout({
                            showStatsStrip: enabled,
                            stats:
                              enabled && (!value.layout.stats || value.layout.stats.length === 0)
                                ? [
                                    { value: '1000+', label: 'Aktif Site' },
                                    { value: '99.9%', label: 'Uptime' },
                                    { value: '7/24', label: 'Destek' },
                                    { value: '12+', label: 'Entegrasyon' },
                                  ]
                                : value.layout.stats,
                          });
                        }}
                      />
                    }
                    label={t('ecommerceCorporateHero.statsStrip')}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    {t('ecommerceCorporateHero.statsStripHint')}
                  </Typography>
                  {value.layout.showStatsStrip &&
                    Array.from({ length: CORPORATE_HERO_MAX_STATS_STRIP }).map((_, index) => (
                      <Grid container spacing={1} key={`strip-stat-${index}`} sx={{ mb: 1 }}>
                        <Grid item xs={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label={t('ecommerceCorporateHero.statValue')}
                            value={value.layout.stats?.[index]?.value || ''}
                            onChange={(e) => updateStripStat(index, { value: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            fullWidth
                            size="small"
                            label={t('ecommerceCorporateHero.statLabel')}
                            value={value.layout.stats?.[index]?.label || ''}
                            onChange={(e) => updateStripStat(index, { label: e.target.value })}
                          />
                        </Grid>
                      </Grid>
                    ))}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!value.layout.showImageSlider}
                        onChange={(e) =>
                          patchLayout({
                            showImageSlider: e.target.checked,
                            slides: e.target.checked
                              ? value.layout.slides?.length
                                ? value.layout.slides
                                : [emptySlide()]
                              : value.layout.slides,
                          })
                        }
                      />
                    }
                    label={t('ecommerceCorporateHero.imageSlider')}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    {t('ecommerceCorporateHero.imageSliderHint')}
                  </Typography>
                  {value.layout.showImageSlider && (
                    <>
                      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>{t('ecommerceCorporateHero.imagePosition')}</InputLabel>
                            <Select
                              label={t('ecommerceCorporateHero.imagePosition')}
                              value={value.layout.imagePosition || 'right'}
                              onChange={(e) =>
                                patchLayout({ imagePosition: e.target.value as 'left' | 'right' })
                              }
                            >
                              <MenuItem value="left">{t('ecommerceCorporateHero.imageLeft')}</MenuItem>
                              <MenuItem value="right">{t('ecommerceCorporateHero.imageRight')}</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={t('ecommerceCorporateHero.sliderAutoplayMs')}
                            value={value.layout.sliderAutoplayMs ?? 5000}
                            onChange={(e) =>
                              patchLayout({
                                sliderAutoplayMs: Math.max(1500, Number(e.target.value) || 5000),
                              })
                            }
                          />
                        </Grid>
                      </Grid>
                      {(value.layout.slides || []).map((slide, index) => (
                        <Box
                          key={`slide-${index}`}
                          sx={{
                            mb: 1.5,
                            p: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                          }}
                        >
                          <Grid container spacing={1.5} alignItems="center">
                            <Grid item xs={12} sm={5}>
                              <TextField
                                fullWidth
                                size="small"
                                label={t('ecommerceCorporateHero.slideTitle')}
                                value={slide.title || ''}
                                onChange={(e) => updateSlide(index, { title: e.target.value })}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label={t('ecommerceCorporateHero.slideImageUrl')}
                                value={slide.imageUrl || ''}
                                onChange={(e) => updateSlide(index, { imageUrl: e.target.value })}
                              />
                            </Grid>
                            <Grid item xs={12} sm={1}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  const next = [...(value.layout.slides || [])];
                                  next.splice(index, 1);
                                  patchLayout({ slides: next });
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Grid>
                            <Grid item xs={12}>
                              <Button
                                size="small"
                                variant="outlined"
                                component="label"
                                startIcon={<CloudUploadIcon />}
                                disabled={uploading}
                              >
                                {uploading && uploadTarget === `slide:${index}`
                                  ? t('ecommerceCorporateHero.uploading')
                                  : t('ecommerceCorporateHero.uploadImage')}
                                <input
                                  type="file"
                                  hidden
                                  accept="image/*"
                                  onChange={(e) => {
                                    const selectedFile = e.target.files?.[0];
                                    if (selectedFile) {
                                      void uploadHeroImage(selectedFile, `slide:${index}`);
                                    }
                                    e.target.value = '';
                                  }}
                                />
                              </Button>
                              {slide.imageUrl && (
                                <Box
                                  component="img"
                                  src={slide.imageUrl}
                                  alt={slide.title || `slide-${index}`}
                                  sx={{
                                    ml: 1.5,
                                    height: 40,
                                    maxWidth: 80,
                                    objectFit: 'contain',
                                    verticalAlign: 'middle',
                                  }}
                                />
                              )}
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                      {(value.layout.slides || []).length < CORPORATE_HERO_MAX_SLIDES && (
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() =>
                            patchLayout({
                              slides: [...(value.layout.slides || []), emptySlide()],
                            })
                          }
                        >
                          {t('ecommerceCorporateHero.addSlide')}
                        </Button>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      )}

      {needsBgImage && (
        <>
          {renderImageField(
            t('ecommerceCorporateHero.backgroundImage'),
            'backgroundImageUrl',
            value.layout.backgroundImageUrl
          )}
          <Grid item xs={12} md={4}>
            {renderColorField(
              t('ecommerceCorporateHero.overlayColor'),
              value.design.overlayColor,
              (color) => patchDesign({ overlayColor: color }),
              '#0f172a',
              '#0f172a'
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" gutterBottom>
              {t('ecommerceCorporateHero.overlayOpacity')}: {Math.round((value.design.overlayOpacity ?? 0.65) * 100)}%
            </Typography>
            <input
              type="range"
              min="0"
              max="100"
              value={Math.round((value.design.overlayOpacity ?? 0.65) * 100)}
              onChange={(e) => patchDesign({ overlayOpacity: Number(e.target.value) / 100 })}
              style={{ width: '100%' }}
            />
          </Grid>
        </>
      )}

      {needsTwoColumns && (
        <>
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight="bold">
              {t('ecommerceCorporateHero.leftColumn')}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t('ecommerceCorporateHero.badgeText')}
              value={value.layout.leftColumn?.badge || ''}
              onChange={(e) =>
                patchLayout({ leftColumn: { ...value.layout.leftColumn, badge: e.target.value } })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t('ecommerceAboutSettings.fields.title')}
              value={value.layout.leftColumn?.title || ''}
              onChange={(e) =>
                patchLayout({ leftColumn: { ...value.layout.leftColumn, title: e.target.value } })
              }
            />
          </Grid>
          <Grid item xs={12}>
            <RichTextEditor
              label={t('ecommerceAboutSettings.fields.description')}
              value={value.layout.leftColumn?.description || ''}
              onChange={(html) =>
                patchLayout({ leftColumn: { ...value.layout.leftColumn, description: html } })
              }
              minHeight={110}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight="bold">
              {t('ecommerceCorporateHero.rightColumn')}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t('ecommerceCorporateHero.badgeText')}
              value={value.layout.rightColumn?.badge || ''}
              onChange={(e) =>
                patchLayout({ rightColumn: { ...value.layout.rightColumn, badge: e.target.value } })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t('ecommerceAboutSettings.fields.title')}
              value={value.layout.rightColumn?.title || ''}
              onChange={(e) =>
                patchLayout({ rightColumn: { ...value.layout.rightColumn, title: e.target.value } })
              }
            />
          </Grid>
          <Grid item xs={12}>
            <RichTextEditor
              label={t('ecommerceAboutSettings.fields.description')}
              value={value.layout.rightColumn?.description || ''}
              onChange={(html) =>
                patchLayout({ rightColumn: { ...value.layout.rightColumn, description: html } })
              }
              minHeight={110}
            />
          </Grid>
        </>
      )}

      {needsStats && [0, 1, 2].map((index) => (
        <React.Fragment key={index}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight="bold">
              {t('ecommerceCorporateHero.statItem', { index: index + 1 })}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t('ecommerceCorporateHero.statValue')}
              value={value.layout.stats?.[index]?.value || ''}
              onChange={(e) => {
                const stats = [...(value.layout.stats || [{}, {}, {}])];
                stats[index] = { ...stats[index], value: e.target.value };
                patchLayout({ stats });
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('ecommerceCorporateHero.statLabel')}
              value={value.layout.stats?.[index]?.label || ''}
              onChange={(e) => {
                const stats = [...(value.layout.stats || [{}, {}, {}])];
                stats[index] = { ...stats[index], label: e.target.value };
                patchLayout({ stats });
              }}
            />
          </Grid>
        </React.Fragment>
      ))}

      <Grid item xs={12} md={6}>
        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
          {t('ecommerceAboutSettings.fields.sectionHeight')}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {value.height || defaultHeight}px
        </Typography>
        <input
          type="range"
          min="300"
          max="800"
          value={value.height || defaultHeight}
          onChange={(e) => patch({ height: parseInt(e.target.value, 10) })}
          style={{ width: '100%', cursor: 'pointer' }}
        />
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button size="small" onClick={() => patch({ height: 350 })}>
            {t('ecommerceAboutSettings.sizes.small')}
          </Button>
          <Button size="small" onClick={() => patch({ height: defaultHeight })}>
            {t('ecommerceAboutSettings.sizes.medium')}
          </Button>
          <Button size="small" onClick={() => patch({ height: 650 })}>
            {t('ecommerceAboutSettings.sizes.large')}
          </Button>
        </Box>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>{t('ecommerceCorporateHero.animation')}</InputLabel>
          <Select
            label={t('ecommerceCorporateHero.animation')}
            value={value.design.animationType || 'none'}
            onChange={(e) => patchDesign({ animationType: e.target.value as CorporateHeroAnimationType })}
          >
            {ANIMATION_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {t(`ecommerceCorporateHero.animations.${option}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
          {t('ecommerceAboutSettings.fields.preview')}
        </Typography>
        {heroEnabled ? (
          <>
            <CorporateHeroPreview
              hero={value}
              titleFallback={t('ecommerceAboutSettings.fields.title')}
              descriptionFallback={t('ecommerceAboutSettings.fields.description')}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('ecommerceAboutSettings.fields.heightWithValue', { value: value.height || defaultHeight })}
            </Typography>
          </>
        ) : (
          <Alert severity="warning">{t('ecommerceCorporateHero.heroDisabledHint')}</Alert>
        )}
      </Grid>
      </Grid>
      </Grid>
    </Grid>
  );
};

export default CorporateHeroSettingsPanel;
